import { mapValues } from 'lodash';

import {
  createErrorDescriptor,
  FETCH_STATUS_ABORTED,
  FETCH_STATUS_FAILED,
  FETCH_STATUS_NONE,
  FETCH_STATUS_PENDING,
  FETCH_STATUS_SUCCESS,
  FetchStatus,
} from '@neptune/shared/common-domain';
import { reconcileArrayWithId } from '@neptune/shared/common-util';
import { BaseLeaderboardEntry, LeaderboardListGroup } from '@neptune/shared/leaderboard-domain';

import { ExperimentAttributeOperation, OperationActions } from 'state/attributes/actions';

import { LeaderboardActionTypes, LeaderboardsActions } from './leaderboards-actions';

export type LeaderboardsState = Record<string, LeaderboardState | undefined>;

export type LeaderboardState<T extends BaseLeaderboardEntry = BaseLeaderboardEntry> = {
  fetchStatus: FetchStatus;
  timestamp: number;
  entries: T[];
  groups?: LeaderboardListGroup[];
  matchingItemCount: number;
  totalGroupCount?: number;
  error?: any;
};

const initialState: LeaderboardsState = {};

export const leaderboardInitialState: LeaderboardState = {
  fetchStatus: FETCH_STATUS_NONE,
  timestamp: 0,
  entries: [],
  groups: [],
  matchingItemCount: 0,
  totalGroupCount: undefined,
  error: undefined,
};

const leaderboardReducer = (
  state = leaderboardInitialState,
  action: LeaderboardsActions | OperationActions,
): LeaderboardState => {
  switch (action.type) {
    case LeaderboardActionTypes.fetchStart: {
      return {
        ...state,
        fetchStatus: FETCH_STATUS_PENDING,
        error: undefined,
      };
    }

    case LeaderboardActionTypes.fetchAbort: {
      return {
        ...state,
        fetchStatus: FETCH_STATUS_ABORTED,
        error: undefined,
      };
    }

    case LeaderboardActionTypes.fetchSuccess: {
      const { entries, groups, matchingItemCount, totalGroupCount, timestamp } = action.payload;

      const stabilizedEntries = reconcileArrayWithId(entries, state.entries);
      const stabilizedGroups = reconcileArrayWithId(groups, state.groups);

      return {
        ...state,
        timestamp,
        entries: stabilizedEntries,
        groups: stabilizedGroups,
        matchingItemCount,
        totalGroupCount,
        fetchStatus: FETCH_STATUS_SUCCESS,
        error: undefined,
      };
    }

    case LeaderboardActionTypes.fetchFail: {
      return {
        ...state,
        fetchStatus: FETCH_STATUS_FAILED,
        error: action.payload.error && createErrorDescriptor(action.payload.error),
      };
    }

    case ExperimentAttributeOperation.success: {
      const { experiment } = action.payload;
      const entries = state.entries.map((entry) => {
        if (entry.id === experiment.id) {
          const newAttributes = entry.attributes.map((attribute) => {
            const updatedAttribute = experiment.attributes.find(
              (expAttr) =>
                expAttr.attributeName === attribute.attributeName &&
                expAttr.attributeType === attribute.attributeType,
            );

            return updatedAttribute ?? attribute;
          });

          return {
            ...experiment,
            attributes: newAttributes,
          };
        }

        return entry;
      });

      return {
        ...state,
        entries,
      };
    }

    default:
      return state;
  }
};

export const leaderboardsReducer = (
  state: LeaderboardsState = initialState,
  action: LeaderboardsActions | OperationActions,
): LeaderboardsState => {
  switch (action.type) {
    case LeaderboardActionTypes.fetchAbort:
    case LeaderboardActionTypes.fetchFail:
    case LeaderboardActionTypes.fetchStart:
    case LeaderboardActionTypes.fetchSuccess:
      return {
        ...state,
        [action.id]: leaderboardReducer(state[action.id], action),
      };

    case ExperimentAttributeOperation.success:
      return mapValues(state, (substate) => leaderboardReducer(substate, action));
  }

  return state;
};
